import { useRef, type ReactNode } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function ExperienceCanvas({ children }: { children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const aura = useRef<HTMLDivElement>(null);

  useGSAP((_context, contextSafe) => {
    const media = gsap.matchMedia();
    media.add({
      allowMotion: "(prefers-reduced-motion: no-preference)",
      finePointer: "(pointer: fine)",
    }, (context) => {
      const { allowMotion, finePointer } = context.conditions as { allowMotion: boolean; finePointer: boolean };
      if (!allowMotion) return;

      const intro = gsap.timeline({ defaults: { ease: "power3.out" } });
      intro
        .from(".topbar", { y: -24, autoAlpha: 0, duration: 0.7, willChange: "transform,opacity", clearProps: "transform,opacity,visibility,willChange" })
        .from(".burnline__kicker", { y: 18, autoAlpha: 0, duration: 0.48 }, 0.18)
        .from(".burnline__title-word", { yPercent: 110, rotation: 2.5, duration: 0.9, stagger: 0.09, willChange: "transform", clearProps: "transform,willChange" }, 0.22)
        .from(".burnline__lede, .burnline__heading-meta", { y: 22, autoAlpha: 0, duration: 0.62, stagger: 0.08 }, 0.46)
        .from(".burnline__aperture", { scale: 0.88, rotation: -4, autoAlpha: 0, duration: 1.1, ease: "power4.out", willChange: "transform,opacity", clearProps: "transform,opacity,visibility,willChange" }, 0.38)
        .fromTo(".burn-metric", { x: 32, autoAlpha: 0 }, {
          x: 0,
          autoAlpha: 1,
          duration: 0.6,
          stagger: 0.07,
          clearProps: "transform,opacity,visibility,willChange",
          willChange: "transform,opacity",
        }, 0.72);

      const revealTargets = root.current?.querySelectorAll<HTMLElement>(
        ".burnstream, .analysis-window, .primary-grid > *, .breakdown-grid, .context-grid > *, .methodology",
      );
      const reveal = (batch: Element[]) => {
        gsap.fromTo(batch, { y: 54, autoAlpha: 0 }, {
          y: 0,
          autoAlpha: 1,
          duration: 0.85,
          ease: "power3.out",
          stagger: 0.08,
          overwrite: true,
          clearProps: "transform,opacity,visibility",
        });
      };
      const revealSafely = contextSafe?.(reveal) ?? reveal;
      const batchTriggers = revealTargets?.length ? ScrollTrigger.batch(revealTargets, {
        start: "top 88%",
        once: true,
        onEnter: revealSafely,
      }) : [];

      gsap.to(".burnline__orbit--outer", {
        rotation: 84,
        ease: "none",
        scrollTrigger: {
          trigger: ".burnline",
          start: "top top",
          end: "bottom top",
          scrub: 0.8,
        },
      });

      gsap.to(".topbar__progress i", {
        scaleX: 1,
        ease: "none",
        scrollTrigger: {
          start: 0,
          end: "max",
          scrub: 0.25,
        },
      });

      let removePointerListener: (() => void) | undefined;
      if (finePointer && aura.current) {
        gsap.set(aura.current, { xPercent: -50, yPercent: -50 });
        const xTo = gsap.quickTo(aura.current, "x", { duration: 0.72, ease: "power3.out" });
        const yTo = gsap.quickTo(aura.current, "y", { duration: 0.72, ease: "power3.out" });
        const followPointer = (event: PointerEvent) => {
          xTo(event.clientX);
          yTo(event.clientY);
        };
        window.addEventListener("pointermove", followPointer, { passive: true });
        removePointerListener = () => window.removeEventListener("pointermove", followPointer);
      }

      return () => {
        removePointerListener?.();
        batchTriggers?.forEach((trigger) => trigger.kill());
      };
    }, root);

    const refreshRaw = () => ScrollTrigger.refresh();
    const refresh = contextSafe?.(refreshRaw) ?? refreshRaw;
    void document.fonts?.ready.then(refresh);
    return () => media.revert();
  }, { scope: root });

  return (
    <div ref={root} className="experience-canvas">
      <div ref={aura} className="pointer-aura" aria-hidden="true" />
      <div className="site-grain" aria-hidden="true" />
      <div className="site-beam" aria-hidden="true" />
      <div className="experience-canvas__content">{children}</div>
    </div>
  );
}
